import { Badge, Button, SkeletonLine } from "@l30/ui";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  useApplyExtraction,
  useDiscardExtraction,
  useExtraction,
  useReviewExtractionItem,
} from "../../api/ideation-maps.js";

import { ConfidenceBadge, MergeBadge } from "./ConfidenceBadge.js";
import {
  extractionItemDetail,
  extractionItemTitle,
  extractionReviewActionState,
  proposalSummary,
} from "./extraction-review-helpers.js";

import type { ExtractionItem } from "@l30/shared";

function ItemRow({
  item,
  onAccept,
  onReject,
  busy,
}: {
  item: ExtractionItem;
  onAccept: () => void;
  onReject: () => void;
  busy: boolean;
}) {
  const detail = extractionItemDetail(item);
  return (
    <li className="flex items-start gap-3 rounded-md border border-border bg-surface px-3 py-2">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-fg">{extractionItemTitle(item)}</span>
          <Badge variant="default">{item.itemType}</Badge>
          <MergeBadge matchKind={item.matchKind} payload={item.payload} />
          <ConfidenceBadge band={item.band} confidence={item.confidence} />
        </div>
        {detail ? <p className="text-xs text-fg-muted">{detail}</p> : null}
      </div>
      <div className="flex shrink-0 gap-1">
        <Button
          type="button"
          variant={item.status === "accepted" ? "primary" : "secondary"}
          size="sm"
          disabled={busy}
          onClick={onAccept}
          aria-pressed={item.status === "accepted"}
        >
          Accept
        </Button>
        <Button
          type="button"
          variant={item.status === "rejected" ? "danger" : "secondary"}
          size="sm"
          disabled={busy}
          onClick={onReject}
          aria-pressed={item.status === "rejected"}
        >
          Reject
        </Button>
      </div>
    </li>
  );
}

/**
 * Unified extraction accept/reject/apply surface (Sheet Step B + legacy wrappers).
 * Bulk Accept must not gate on a stuck `mutation.isPending` — use local busy only.
 * Remount via `key={extractionId}` at the call site (wrapper below) to reset busy.
 */
function ExtractionReviewBodyInner(props: {
  extractionId: string;
  onApplied: () => void;
  onDiscard: () => void;
  persistDiscard?: boolean;
}) {
  const { extractionId, onApplied, onDiscard, persistDiscard = false } = props;

  const { data, isLoading } = useExtraction(extractionId);
  const review = useReviewExtractionItem(extractionId);
  const applyMutation = useApplyExtraction();
  const discardMutation = useDiscardExtraction();
  const [bulkBusy, setBulkBusy] = useState(false);

  async function handleDiscard() {
    if (!persistDiscard) {
      onDiscard();
      return;
    }
    try {
      await discardMutation.mutateAsync(extractionId);
      toast.success("Extraction discarded");
      onDiscard();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not discard extraction");
    }
  }

  if (isLoading && !data) {
    return (
      <div className="space-y-2" aria-label="Loading proposal">
        <SkeletonLine className="w-1/2" />
        <SkeletonLine className="w-2/3" />
      </div>
    );
  }

  if (!data || data.status === "processing") {
    return (
      <div
        className="flex items-center gap-2 rounded-md border bg-surface-muted p-3 text-sm text-fg-muted"
        aria-label="Extraction in progress"
      >
        <Sparkles className="h-4 w-4 animate-pulse text-brand" aria-hidden="true" />
        Extracting proposals… this can take up to a minute for a long document.
      </div>
    );
  }

  if (data.status === "failed") {
    return (
      <div className="flex flex-col gap-3">
        <p className="rounded-md border border-danger-border bg-danger-bg p-3 text-sm text-danger-fg">
          Extraction failed — the agent errored. Try again, or discard.
        </p>
        <div>
          <Button type="button" variant="ghost" size="sm" onClick={() => void handleDiscard()}>
            Discard
          </Button>
        </div>
      </div>
    );
  }

  if (data.status === "applied") {
    return (
      <div className="rounded-md border bg-surface-muted p-3 text-sm text-fg-muted">
        This extraction is already applied.
      </div>
    );
  }

  return (
    <ReviewReady
      extractionId={extractionId}
      items={data.items}
      bulkBusy={bulkBusy}
      setBulkBusy={setBulkBusy}
      review={review}
      applyMutation={applyMutation}
      discardBusy={discardMutation.isPending}
      onApply={() => {
        applyMutation.mutate(extractionId, {
          onSuccess: () => {
            toast.success("Applied to map");
            onApplied();
          },
          onError: (err) => toast.error(err.message || "Could not apply extraction"),
        });
      }}
      onDiscard={() => void handleDiscard()}
    />
  );
}

function ReviewReady({
  extractionId: _extractionId,
  items,
  bulkBusy,
  setBulkBusy,
  review,
  applyMutation,
  discardBusy,
  onApply,
  onDiscard,
}: {
  extractionId: string;
  items: ExtractionItem[];
  bulkBusy: boolean;
  setBulkBusy: (v: boolean) => void;
  review: ReturnType<typeof useReviewExtractionItem>;
  applyMutation: ReturnType<typeof useApplyExtraction>;
  discardBusy: boolean;
  onApply: () => void;
  onDiscard: () => void;
}) {
  const actions = extractionReviewActionState(items);
  const footerBusy = bulkBusy || applyMutation.isPending || discardBusy;

  async function handleAcceptAllPending() {
    const pending = items.filter((i) => i.status === "pending");
    if (pending.length === 0) return;
    setBulkBusy(true);
    try {
      for (const item of pending) {
        await review.mutateAsync({ itemId: item.id, status: "accepted" });
      }
      toast.success(
        `Accepted ${pending.length} proposal${pending.length === 1 ? "" : "s"} — click Apply`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not accept all pending proposals");
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleAcceptAllAuto() {
    const autoPending = items.filter((i) => i.band === "auto" && i.status === "pending");
    if (autoPending.length === 0) return;
    setBulkBusy(true);
    try {
      for (const item of autoPending) {
        await review.mutateAsync({ itemId: item.id, status: "accepted" });
      }
      toast.success(
        `Accepted ${autoPending.length} high-confidence proposal${autoPending.length === 1 ? "" : "s"} — click Apply`,
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not accept high-confidence proposals",
      );
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-fg-muted">{proposalSummary(items)}</p>

      {actions.showApplyBanner ? (
        <div
          className="rounded-md border border-info-border bg-info-bg px-3 py-2 text-sm text-info-fg"
          role="status"
        >
          {actions.acceptedCount} accepted — Apply to write to the map, or go Back to the inbox.
        </div>
      ) : null}

      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            busy={footerBusy}
            onAccept={() =>
              review.mutate(
                { itemId: item.id, status: "accepted" },
                {
                  onError: (err) => toast.error(err.message || "Could not accept proposal"),
                },
              )
            }
            onReject={() =>
              review.mutate(
                { itemId: item.id, status: "rejected" },
                {
                  onError: (err) => toast.error(err.message || "Could not reject proposal"),
                },
              )
            }
          />
        ))}
      </ul>

      <ReviewFooter
        actions={actions}
        bulkBusy={bulkBusy}
        footerBusy={footerBusy}
        applyPending={applyMutation.isPending}
        onAcceptAllPending={() => void handleAcceptAllPending()}
        onAcceptAllAuto={() => void handleAcceptAllAuto()}
        onApply={onApply}
        onDiscard={onDiscard}
      />
    </div>
  );
}

function ReviewFooter({
  actions,
  bulkBusy,
  footerBusy,
  applyPending,
  onAcceptAllPending,
  onAcceptAllAuto,
  onApply,
  onDiscard,
}: {
  actions: ReturnType<typeof extractionReviewActionState>;
  bulkBusy: boolean;
  footerBusy: boolean;
  applyPending: boolean;
  onAcceptAllPending: () => void;
  onAcceptAllAuto: () => void;
  onApply: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="sticky bottom-0 -mx-1 flex flex-wrap gap-2 border-t border-border bg-surface px-1 pt-3 pb-1">
      <Button
        type="button"
        variant="primary"
        size="sm"
        disabled={footerBusy || !actions.canAcceptAllPending}
        onClick={onAcceptAllPending}
        aria-label="Accept all pending"
        title={
          actions.pendingCount === 0
            ? "No pending proposals"
            : `Accept ${actions.pendingCount} pending proposal${actions.pendingCount === 1 ? "" : "s"} (then click Apply)`
        }
      >
        {bulkBusy ? "Accepting…" : `Accept all pending (${actions.pendingCount})`}
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={footerBusy || !actions.canAcceptAllHighConfidence}
        onClick={onAcceptAllAuto}
        aria-label="Accept all auto"
        title={
          actions.autoPendingCount === 0
            ? "None in the high-confidence (auto) band — use Accept all pending or Accept per row"
            : `Accept ${actions.autoPendingCount} high-confidence proposal${actions.autoPendingCount === 1 ? "" : "s"}`
        }
      >
        Accept all high-confidence
        {actions.autoPendingCount > 0 ? ` (${actions.autoPendingCount})` : ""}
      </Button>
      <Button
        type="button"
        variant={actions.showApplyBanner ? "primary" : "secondary"}
        size="sm"
        disabled={!actions.canApply || footerBusy}
        onClick={onApply}
        title={
          actions.canApply
            ? "Write accepted proposals onto the map"
            : "Accept at least one proposal first"
        }
      >
        {applyPending ? "Applying…" : "Apply"}
      </Button>
      <Button type="button" variant="ghost" size="sm" disabled={footerBusy} onClick={onDiscard}>
        Discard
      </Button>
    </div>
  );
}

export function ExtractionReviewBody(props: {
  extractionId: string;
  onApplied: () => void;
  onDiscard: () => void;
  persistDiscard?: boolean;
}) {
  return <ExtractionReviewBodyInner key={props.extractionId} {...props} />;
}
